import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Box, Button, Container, Grid, Typography,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BarChartIcon from '@mui/icons-material/BarChart';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { TrendingUp, ArrowForward, Leaderboard, Gavel } from '@mui/icons-material';
import BackgroundAnimation from '../components/BackgroundAnimation';
import { Surface } from '../components/primitives';

const pulseAnimation = keyframes`
  0%   { transform: scale(0); opacity: 0.6; }
  100% { transform: scale(4); opacity: 0; }
`;

const PulseEffect = styled('div')({
    position: 'fixed',
    borderRadius: '50%',
    pointerEvents: 'none',
    width: 160,
    height: 160,
    background: 'radial-gradient(circle, rgba(99,102,241,0.4) 0%, rgba(99,102,241,0) 70%)',
    animation: `${pulseAnimation} 1s ease-out forwards`,
    zIndex: 1,
});

const BackgroundContainer = styled(Box)({
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: -1,
    overflow: 'hidden',
    pointerEvents: 'none',
    '& > *': { pointerEvents: 'auto' },
});

const MotionContainer = motion(Container);
const MotionTypography = motion(Typography);
const MotionBox = motion(Box);
const MotionSurface = motion(Surface);

const Home = ({ dollarsEnabled }) => {
    const [pulses, setPulses] = useState([]);

    const handleClick = (e) => {
        const newPulse = { id: Date.now(), x: e.clientX, y: e.clientY };
        setPulses(prev => [...prev, newPulse]);
        setTimeout(() => setPulses(prev => prev.filter(p => p.id !== newPulse.id)), 1000);
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 16 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, staggerChildren: 0.08 } },
    };
    const itemVariants = {
        hidden: { opacity: 0, y: 16 },
        visible: { opacity: 1, y: 0 },
    };

    const featureCards = [
        { icon: <Gavel />, title: 'Congress Trade Tracking', description: 'Track congressional trading activities as they happen.', link: '/recent-trades' },
        { icon: <Leaderboard />, title: 'Congress Leaderboard', description: 'Discover which Congress members are the most successful traders.', link: '/leaderboard' },
        { icon: <TrendingUp />, title: 'Stock Rankings', description: 'Access rankings of stocks based on Congress members\' activities.', link: '/stocks' },
    ];

    const offerCards = [
        { icon: <BarChartIcon />, title: 'Comprehensive Data', description: 'See what big moves Congress is making in the stock market right now.' },
        { icon: <NotificationsActiveIcon />, title: 'Custom Trade Alerts', description: 'Get notified as soon as your favorite Congress members make stock trades.' },
        { icon: <ManageSearchIcon />, title: 'Money in Politics', description: 'Compare Congress members\' stock trades with policies they support.' },
    ];

    const plans = [
        {
            title: 'Free',
            price: '$0',
            sub: 'Forever',
            features: [
                'All Congress stock trades, no delay',
                'Issuer pages & trading analytics',
                'Congress Trading Leaderboard',
            ],
            cta: 'Sign Up Free',
            ctaTo: '/sign-up',
        },
        {
            title: 'Pro',
            price: '$8',
            sub: 'per month',
            features: [
                'Top congressional stock picks & rankings',
                'Congressional options activity',
                'Member tracking & real-time trade alerts',
                'Weekly digest email',
            ],
            cta: 'Upgrade Now',
            ctaTo: '/pricing',
            featured: true,
        },
    ];

    const faqs = [
        { q: 'How are Congress stock trades collected?', a: 'Directly from publicly available sources — the House and Senate disclosure websites where members are required to file stock trades by law.' },
        { q: 'How often does Congress report trades?', a: 'Under the STOCKS Act, members must report each stock trade within 45 days of the transaction.' },
        { q: 'How does the stock ranking algorithm work?', a: 'It analyzes Congress members\' trading patterns, focusing on long-term holding potential and historical performance.' },
    ];

    return (
        <Box sx={{ position: 'relative', zIndex: 0 }} onClick={handleClick}>
            <BackgroundContainer sx={{
                background: t => `radial-gradient(ellipse at top, ${t.palette.custom.primary[700]}18 0%, transparent 60%)`,
            }} />

            {pulses.map(pulse => (
                <PulseEffect key={pulse.id} style={{ left: pulse.x - 80, top: pulse.y - 80 }} />
            ))}

            <BackgroundAnimation dollarsEnabled={dollarsEnabled} />

            <Box sx={{ position: 'relative', zIndex: 1 }}>

                {/* ── Hero ── */}
                <MotionBox
                    component="section"
                    sx={{ minHeight: '85vh', display: 'flex', alignItems: 'center', position: 'relative' }}
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <MotionContainer maxWidth="lg" sx={{ textAlign: 'center' }}>
                        <MotionTypography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.75rem', lg: '4.25rem' },
                                lineHeight: 1.05,
                                letterSpacing: '-0.045em',
                                mb: 3,
                                color: 'text.primary',
                            }}
                            variants={itemVariants}
                        >
                            Trade like Congress does.
                        </MotionTypography>
                        <MotionTypography
                            variant="body1"
                            sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '1rem', md: '1.1875rem' },
                                maxWidth: 620,
                                mx: 'auto',
                                mb: 5,
                                lineHeight: 1.55,
                            }}
                            variants={itemVariants}
                        >
                            Capitol Gains democratizes access to the stock trades filed by U.S. Congress members —
                            so you can see what the insiders are buying and selling, in real time.
                        </MotionTypography>
                        <MotionBox
                            sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}
                            variants={itemVariants}
                        >
                            <Button variant="contained" size="large" component={Link} to="/recent-trades" endIcon={<ArrowForward />}>
                                View Recent Trades
                            </Button>
                            <Button variant="outlined" size="large" component={Link} to="/about">
                                Learn More
                            </Button>
                        </MotionBox>
                    </MotionContainer>
                </MotionBox>

                {/* ── Explore cards ── */}
                <Box component="section" sx={{ py: 10 }}>
                    <MotionContainer
                        maxWidth="lg"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={containerVariants}
                    >
                        <Typography variant="h2" align="center" sx={{ color: 'text.primary', mb: 1 }}>
                            Explore Congressional Trading
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mb: 6 }}>
                            Three ways to follow the money.
                        </Typography>
                        <Grid container spacing={2}>
                            {featureCards.map((feature, index) => (
                                <Grid item xs={12} md={4} key={index}>
                                    <MotionSurface
                                        component={Link}
                                        to={feature.link}
                                        variants={itemVariants}
                                        interactive
                                        padding={3.5}
                                        sx={{
                                            display: 'block',
                                            textDecoration: 'none',
                                            height: '100%',
                                        }}
                                    >
                                        <Box sx={{ color: 'primary.light', mb: 1.5, '& svg': { fontSize: 22 } }}>
                                            {feature.icon}
                                        </Box>
                                        <Typography variant="h4" sx={{ color: 'text.primary', mb: 1 }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                            {feature.description}
                                        </Typography>
                                    </MotionSurface>
                                </Grid>
                            ))}
                        </Grid>
                    </MotionContainer>
                </Box>

                {/* ── What we offer ── */}
                <Box component="section" sx={{ py: 10 }}>
                    <MotionContainer
                        maxWidth="lg"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={containerVariants}
                    >
                        <Typography variant="h2" align="center" sx={{ color: 'text.primary', mb: 1 }}>
                            What Capitol Gains offers
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mb: 6 }}>
                            Purpose-built tools for following Congress's money.
                        </Typography>
                        <Grid container spacing={2}>
                            {offerCards.map((item, index) => (
                                <Grid item xs={12} md={4} key={index}>
                                    <MotionSurface
                                        variants={itemVariants}
                                        padding={3.5}
                                        sx={{ height: '100%' }}
                                    >
                                        <Box sx={{ color: 'primary.light', mb: 1.5, '& svg': { fontSize: 22 } }}>
                                            {item.icon}
                                        </Box>
                                        <Typography variant="h4" sx={{ color: 'text.primary', mb: 1 }}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                            {item.description}
                                        </Typography>
                                    </MotionSurface>
                                </Grid>
                            ))}
                        </Grid>
                    </MotionContainer>
                </Box>

                {/* ── Pricing teaser ── */}
                <Box component="section" sx={{ py: 10 }}>
                    <MotionContainer
                        maxWidth="md"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={containerVariants}
                    >
                        <Typography variant="h2" align="center" sx={{ color: 'text.primary', mb: 1 }}>
                            Simple pricing
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mb: 6 }}>
                            Two plans. No credit card required to start.
                        </Typography>
                        <Grid container spacing={2}>
                            {plans.map((plan, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <MotionSurface
                                        variant={plan.featured ? 'accent' : 'default'}
                                        variants={itemVariants}
                                        padding={3.5}
                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    >
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="overline" sx={{ color: plan.featured ? 'primary.light' : 'text.secondary' }}>
                                                {plan.title}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 3 }}>
                                            <Typography variant="h1" sx={{ color: 'text.primary', fontSize: '2.25rem' }}>
                                                {plan.price}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.muted' }}>
                                                {plan.sub}
                                            </Typography>
                                        </Box>
                                        <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none', flex: 1 }}>
                                            {plan.features.map((feature, j) => (
                                                <Box component="li" key={j} sx={{ display: 'flex', alignItems: 'center', mb: 1.25, gap: 1 }}>
                                                    <ArrowForward sx={{ fontSize: 14, color: 'primary.light' }} />
                                                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{feature}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                        <Button
                                            variant={plan.featured ? 'contained' : 'outlined'}
                                            fullWidth
                                            sx={{ mt: 3 }}
                                            component={Link}
                                            to={plan.ctaTo}
                                        >
                                            {plan.cta}
                                        </Button>
                                    </MotionSurface>
                                </Grid>
                            ))}
                        </Grid>
                    </MotionContainer>
                </Box>

                {/* ── FAQ ── */}
                <Box component="section" sx={{ py: 10 }}>
                    <MotionContainer
                        maxWidth="md"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={containerVariants}
                    >
                        <Typography variant="h2" align="center" sx={{ color: 'text.primary', mb: 1 }}>
                            Frequently asked
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mb: 6 }}>
                            Quick answers to the most common questions.
                        </Typography>
                        <Grid container spacing={2}>
                            {faqs.map((faq, i) => (
                                <Grid item xs={12} key={i}>
                                    <MotionSurface variants={itemVariants} padding={3}>
                                        <Typography variant="h5" sx={{ color: 'text.primary', mb: 1 }}>
                                            {faq.q}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                            {faq.a}
                                        </Typography>
                                    </MotionSurface>
                                </Grid>
                            ))}
                        </Grid>
                    </MotionContainer>
                </Box>

                {/* ── CTA ── */}
                <Box component="section" sx={{ py: 10 }}>
                    <MotionContainer
                        maxWidth="md"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={containerVariants}
                    >
                        <Surface variant="accent" padding={{ xs: 4, md: 6 }} sx={{ textAlign: 'center' }}>
                            <Typography variant="h2" sx={{ color: 'text.primary', mb: 1.5 }}>
                                Ready to get started?
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 500, mx: 'auto' }}>
                                Join thousands of investors tracking the biggest moves Congress is making.
                                Free to sign up — no credit card required.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                component={Link}
                                to="/sign-up"
                                endIcon={<ArrowForward />}
                            >
                                Create free account
                            </Button>
                        </Surface>
                    </MotionContainer>
                </Box>
            </Box>
        </Box>
    );
};

export default Home;
